import { Color, Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import { ModalBody, ModalContainer } from 'dashboard/layouts/ModalLayout';
import { QueryType, RelativeTimeSpan } from 'dashboard/models/analytics-query-template';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import FormField from 'design-system/components/FormField';
import { Input } from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import { Paragraph } from 'design-system/components/Text';

import {
  CUSTOM_INTERVAL_MAX,
  CUSTOM_INTERVAL_MIN,
  INTERVAL_OPTIONS,
  INTERVAL_OPTIONS_TO_DATE_RANGE,
  IntervalOptionsValues,
  StandardQueryTemplateIntervalOptions,
} from './constants';

interface CreateQueryTemplateModalProps {
  queryType?: QueryType;
  estimatedDateRangeIntervalInDays: number;
  cancelAction: () => void;
  saveAction: (
    name: string,
    relativeTimeSpan: RelativeTimeSpan,
    queryType?: QueryType,
  ) => Promise<void>;
}

const StyledModalContainer = styled(ModalContainer)`
  flex-direction: row;
  padding-top: 0;
`;

const StyledModalBody = styled(ModalBody)`
  padding: 0;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const StyledFormField = styled(FormField)`
  width: 100%;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: ${Spacing[3]};
`;

const ContentContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${Spacing[3]} ${Spacing[4]};
`;

const InputContainer = styled(ContentContainer)`
  flex-direction: column;
  align-items: start;
`;

const CustomIntervalContainer = styled(ContentContainer)`
  justify-content: flex-start;
  gap: ${Spacing[3]};
  background: ${Color.CoreSlate100};
  border-radius: ${Spacing[1]};
`;

const ButtonGroup = styled(ContentContainer)`
  display: flex;
  justify-content: flex-end;

  & > button:last-child {
    margin-left: ${Spacing[3]};
  }
`;

const Title = styled(Card.Title)``;

const StyledInput = styled(Input)`
  width: 15%;
`;

const CreateQueryTemplateModal: FC<CreateQueryTemplateModalProps> = ({
  queryType,
  estimatedDateRangeIntervalInDays,
  cancelAction,
  saveAction,
}) => {
  const [interval, setInterval] = useState<string>();
  const [customInterval, setCustomInterval] = useState<number>(
    estimatedDateRangeIntervalInDays <= CUSTOM_INTERVAL_MAX
      ? estimatedDateRangeIntervalInDays
      : CUSTOM_INTERVAL_MIN,
  );
  const isCustomIntervalSelected = interval === IntervalOptionsValues.Custom;

  const [name, setName] = useState<string>();

  const handleOnNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleSave = useCallback(async () => {
    if (name != null && interval != null) {
      let relativeTimeSpan =
        INTERVAL_OPTIONS_TO_DATE_RANGE[interval as StandardQueryTemplateIntervalOptions];

      // If the interval is 'custom', handle it separately
      if (interval === IntervalOptionsValues.Custom) {
        relativeTimeSpan = {
          interval: customInterval,
          unit: 'days',
        };
      }

      await saveAction(name, relativeTimeSpan, queryType);
    }
  }, [name, interval, customInterval, saveAction, queryType]);

  if (queryType == null) {
    return null;
  }

  const handleCustomIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const enteredValue = Number(event.target.value);
    if (
      (enteredValue >= CUSTOM_INTERVAL_MIN && enteredValue <= CUSTOM_INTERVAL_MAX) ||
      event.target.value === ''
    ) {
      setCustomInterval(enteredValue);
    }
  };

  return (
    <StyledModalContainer data-test='query-template-create-modal'>
      <StyledModalBody>
        <ContentContainer>
          <Title>Save query</Title>
        </ContentContainer>
        <StyledDivider full={true} />
        <InputContainer>
          <StyledParagraph size='sm'>
            To save a new query, please provide a unique name and select a time interval. Time
            intervals enable saved queries to be used for automation such as creating monthly
            analytics reports.
          </StyledParagraph>
          <StyledFormField label='Query Name'>
            <Input type='text' onChange={handleOnNameChange} />
          </StyledFormField>
          <StyledFormField label='Time Interval'>
            <Select placeholder='Select an option' onValueChange={setInterval}>
              {INTERVAL_OPTIONS.map((opt, i) => (
                <Option key={`interval-options-${i}-${opt.label}`} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </StyledFormField>
          {isCustomIntervalSelected && (
            <CustomIntervalContainer>
              Last
              <StyledInput
                id='metrics-create-query-temaplte__custom-interval-input'
                size='sm'
                type='number'
                value={customInterval.toString()}
                min={CUSTOM_INTERVAL_MIN.toString()}
                max={CUSTOM_INTERVAL_MAX.toString()}
                onChange={handleCustomIntervalChange}
                disabled={!isCustomIntervalSelected}
              />
              {customInterval > 1 ? 'days' : 'day'}
            </CustomIntervalContainer>
          )}
        </InputContainer>
        <StyledDivider full={true} />
        <ButtonGroup>
          <Button
            id='metrics-cancel_query_template_create'
            date-test='metrics-cancel_query_template_create'
            width='auto'
            variant='basic'
            onClick={cancelAction}
          >
            Cancel
          </Button>
          <Button
            id='metrics-create_query_template'
            data-test='metrics-create_query_template'
            width='auto'
            variant='primary'
            onClick={handleSave}
            disabled={!name || !interval || (isCustomIntervalSelected && !customInterval)}
          >
            Save
          </Button>
        </ButtonGroup>
      </StyledModalBody>
    </StyledModalContainer>
  );
};

export default CreateQueryTemplateModal;
